import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import CardFormWrapper from './CardFormWrapper';
import { supabase } from "./supabaseClient";
import { deleteItem } from "./services/deleteItem";
import { fetchUserCards } from "./services/fetchUserCards";
import './App.css';
import AccountSettingsModal from './components/AccountSettingsModal';
import AppHeader from "./components/layout/AppHeader";
import AppSidebar from "./components/layout/AppSidebar";
import AppRoutes from "./routes/AppRoutes";
import useNotifications from "./components/hooks/useNotifications";
import NotificationPanel from "./components/NotificationPanel";
import HobbliVisionModal from "./components/hobblivision/HobbliVisionModal";
import fetchMarketplaceSellerAccess, {
  EMPTY_MARKETPLACE_SELLER_ACCESS,
} from "./services/marketplace/fetchMarketplaceSellerAccess";


const HEADER_CONFIG = [
  {
    match: (path) => path.startsWith("/holladai"),
    logo: "/Logos/HolladAI.png",
    home: "/holladai",
    menuType: "holladai",
  },
  {
    match: () => true, // fallback
    logo: "/Logos/Hobbliclearbackground.png",
    home: "/",
    menuType: "hobbli",
  },
];

function App() {
  // ------------------------
  // State
  // ------------------------
  const [token, setToken] = useState(localStorage.getItem('token') || null);
  const [username, setUsername] = useState(null);
  const [email, setEmail] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  const [cards, setCards] = useState([]);
  const [userItemCount, setUserItemCount] = useState(0);
  const [collections, setCollections] = useState([]);

  const [showMenu, setShowMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [canAccessAdminConsole, setCanAccessAdminConsole] = useState(false);
  const [sellerAccess, setSellerAccess] = useState(
    EMPTY_MARKETPLACE_SELLER_ACCESS
  );
  const [showAddCard, setShowAddCard] = useState(false);
  const [addCardData, setAddCardData] = useState(null);

  const [authMode, setAuthMode] = useState('login');
  // Initialize from localStorage
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem("darkMode") === "true";
  });

  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [updatingAccount, setUpdatingAccount] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState("");
  const [showNotifications, setShowNotifications] = useState(false);

  const [showHobbliVision, setShowHobbliVision] = useState(false);

  const navigate = useNavigate();

  // Canonical collection refresh. All collection views consume `cards` from App.
  const refreshCards = useCallback(async (userId = currentUser?.id) => {
    if (!userId) {
      setCards([]);
      return [];
    }

    try {
      const refreshedCards = await fetchUserCards(userId);
      setCards(refreshedCards || []);
      return refreshedCards || [];
    } catch (err) {
      console.error("Failed to refresh cards:", err);
      return [];
    }
  }, [currentUser?.id]);


  // Auto-redirect to dashboard if logged in and currently at /login
  useEffect(() => {
    if (token && window.location.pathname === "/login") {
      navigate("/", { replace: true });
    }
  }, [token, navigate]);

  // ------------------------
  // Effects
  // ------------------------
  // Dark mode on load
  useEffect(() => {
      document.body.classList.toggle("dark-mode", isDarkMode);
      localStorage.setItem("darkMode", isDarkMode.toString());
    }, [isDarkMode]);


  useEffect(() => {
  let active = true;

  const checkSession = async () => {
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;

      const session = data?.session;

      if (session?.user) {
        setToken(session.access_token);
        setEmail(session.user.email);

        // Fetch full user row AFTER confirming session
        const { data: userData, error: userError } = await supabase
          .from("users")
          .select("*")
          .eq("id", session.user.id)
          .single();

        if (userError) throw userError;
        if (!active) return;

        setCurrentUser(userData);
        setUsername(userData.display_name || userData.email);

        await refreshCards(userData.id);

        if (!active) return;

        fetchUnreadMessages(userData.id);
        fetchCardCounts(userData.id);
        fetchCollections(userData.id);
      } else {
        setToken(null);
        setCurrentUser(null);
        setUsername(null);
        setIsAdmin(false);
        setSellerAccess(EMPTY_MARKETPLACE_SELLER_ACCESS);
        setCards([]);
        setUserItemCount(0);
        setCollections([]);
      }
    } catch (err) {
      console.error("Error checking session:", err);
      localStorage.removeItem("token");
      setToken(null);
      setCurrentUser(null);
      setUsername(null);
      setIsAdmin(false);
      setSellerAccess(EMPTY_MARKETPLACE_SELLER_ACCESS);
      setCards([]);
      setUserItemCount(0);
      setCollections([]);
    } finally {
      if (active) setSessionChecked(true);
    }
  };

  checkSession();

  const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
    if (!active) return;

    setToken(session?.access_token || null);
    setEmail(session?.user?.email || null);

    if (session?.user) {
      supabase
        .from("users")
        .select("*")
        .eq("id", session.user.id)
        .single()
        .then(async ({ data: userData, error }) => {
          if (!active) return;

          if (error) {
            console.error("Failed to fetch user for menu:", error);
            setCurrentUser(null);
            setUsername(null);
          } else {
            setCurrentUser(userData);
            setUsername(userData.display_name || userData.email);
    
            await refreshCards(userData.id);

            if (!active) return;

            fetchCardCounts(userData.id);
            fetchCollections(userData.id);
          }
        });
    } else {
      setCurrentUser(null);
      setUsername(null);
      setIsAdmin(false);
      setSellerAccess(EMPTY_MARKETPLACE_SELLER_ACCESS);
      setToken(null);
      setCards([]);
      setUserItemCount(0);
      setCollections([]);
    }
  });

  return () => {
    active = false;
    subscription?.unsubscribe?.();
  };
}, []);

  // Separate canonical-image access from full marketplace/admin privileges.
  useEffect(() => {
    let active = true;
    setCanAccessAdminConsole(false);
    if (!currentUser?.id) return () => { active = false; };

    const checkConsoleAccess = async () => {
      const { data, error } = await supabase.rpc("can_manage_canonical_images");
      if (!active) return;
      if (error) console.error("Unable to verify admin console access:", error);
      setCanAccessAdminConsole(!error && data === true);
    };
    checkConsoleAccess();
    return () => { active = false; };
  }, [currentUser?.id, currentUser?.user_level_id]);

  // Record genuine app activity without writing on every click/page view.
  // The database RPC only permits a signed-in user to touch their own row.
  useEffect(() => {
    if (!currentUser?.id) return undefined;

    let stopped = false;
    let lastSentAt = 0;
    const HEARTBEAT_MS = 5 * 60 * 1000;

    const recordActivity = async (force = false) => {
      if (stopped || document.visibilityState === "hidden") return;

      const now = Date.now();
      if (!force && now - lastSentAt < HEARTBEAT_MS) return;
      lastSentAt = now;

      const { error } = await supabase.rpc("touch_user_last_active");
      if (error) {
        console.warn("Unable to update last active timestamp:", error);
      }
    };

    recordActivity(true);
    const interval = window.setInterval(() => recordActivity(false), HEARTBEAT_MS);
    const handleVisibility = () => {
      if (document.visibilityState === "visible") recordActivity(false);
    };
    const handleFocus = () => recordActivity(false);

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleFocus);

    return () => {
      stopped = true;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleFocus);
    };
  }, [currentUser?.id]);

  useEffect(() => {
    let active = true;

    const loadSellerAccess = async () => {
      if (!currentUser?.id) {
        setSellerAccess(EMPTY_MARKETPLACE_SELLER_ACCESS);
        setIsAdmin(false);
        return;
      }

      try {
        const access = await fetchMarketplaceSellerAccess();

        if (!active) return;

        setSellerAccess(access);
        setIsAdmin(access.isAdmin);
      } catch (error) {
        console.error("Failed to load seller/admin access:", error);

        if (!active) return;

        setSellerAccess(EMPTY_MARKETPLACE_SELLER_ACCESS);
        setIsAdmin(false);
      }
    };

    loadSellerAccess();

    return () => {
      active = false;
    };
  }, [currentUser?.id]);


const {
  unreadCount,
  notifications,
  markAsRead,
  markAllAsRead
} = useNotifications(currentUser);

 useEffect(() => {
    if (!currentUser) return;

    fetchUnreadMessages(currentUser.id);

    const interval = setInterval(() => {
      fetchUnreadMessages(currentUser.id);
    }, 10000); // every 10s

    return () => clearInterval(interval);
  }, [currentUser]);

    useEffect(() => {
      if (currentUser) {
        setNewDisplayName(currentUser.display_name || "");
        setNewEmail(currentUser.email || "");
      }
    }, [currentUser]);


  // Fetch collections
  const fetchCollections = async (userId) => {
    if (!userId) return;
    try {
      const { data, error } = await supabase
        .from("collections")
        .select("id, name, user_id")
        .eq("user_id", userId);

      if (error) throw error;
      setCollections(data || []);
    } catch (err) {
      console.error("Error fetching collections:", err.message);
    }
  };

  // Fetch card counts
  const fetchCardCounts = async (userId) => {
    if (!userId) return;
    try {
      const { count, error } = await supabase
        .from("user_items")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userId);

      if (error) throw error;
      setUserItemCount(count || 0);
    } catch (err) {
      console.error("Error fetching card counts:", err);
    }
  };

  // fetch unread messages count
  const fetchUnreadMessages = async (userId) => {
    if (!userId) return;

    const { count, error } = await supabase
      .from("messages")
      .select("*", { count: "exact", head: true })
      .eq("receiver_id", userId)
      .is("read_at", null);

    if (error) {
      console.error("Error fetching unread messages:", error);
      return;
    }


  };

  // ------------------------
  // Auth Handlers
  // ------------------------
    const handleRegisterSupabase = async (emailInput, passwordInput, displayNameInput) => {
      try {
        // Create auth account (with optional metadata)
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email: emailInput,
          password: passwordInput,
          options: {
            data: { display_name: displayNameInput },
            emailRedirectTo: "https://www.hobbli.com/login",
          },
        });

        if (signUpError) throw signUpError;

        const user = signUpData.user;
        if (!user) throw new Error("User creation failed. Please try again.");

        setAuthMode("login");
      } catch (err) {
        console.error("Registration error:", err);
        throw err;
      }
    };

    const handleLoginSupabase = async (emailInput, passwordInput) => {
      try {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: emailInput,
          password: passwordInput,
        });
        if (error) throw error;

        const jwt = data.session?.access_token;
        if (!jwt) throw new Error("Login failed: no session returned");

        localStorage.setItem("token", jwt);
        setToken(jwt);

        const userId = data.session.user.id;
        await refreshCards(userId);
        fetchCardCounts(userId);

      } catch (err) {
        console.error("Login error:", err);
        throw err;
      }
    };

    
  const handleLogout = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem("token");

    setToken(null);
    setCurrentUser(null);
    setUsername(null);
    setEmail(null);
    setIsAdmin(false);
    setSellerAccess(EMPTY_MARKETPLACE_SELLER_ACCESS);
    setCards([]);
    setUserItemCount(0);
    setCollections([]);
  };

    const handleUpdateAccount = async (e) => {
    e?.preventDefault?.();

    try {
      setUpdatingAccount(true);

      // Email is currently displayed as read-only in Account Settings, but keep
      // validation here in case editable email returns later.
      if (newEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
        throw new Error("Please enter a valid email address.");
      }

      // Password fields live inside AccountSettingsModal. Treat either populated
      // field as an attempted password change so App does not need duplicate UI state.
      const changingPassword = Boolean(newPassword || confirmPassword);
      if (changingPassword) {
        if (!newPassword || !confirmPassword) {
          throw new Error("Please enter and confirm your new password.");
        }
        if (newPassword !== confirmPassword) {
          throw new Error("Passwords do not match.");
        }
        if (newPassword.length < 8) {
          throw new Error("Password must be at least 8 characters long.");
        }
      }

      if (newDisplayName && newDisplayName !== currentUser.display_name) {
        const { error } = await supabase
          .from("users")
          .update({ display_name: newDisplayName })
          .eq("id", currentUser.id);
        if (error) throw error;
      }

      const authPayload = {};
      if (newEmail && newEmail !== email) authPayload.email = newEmail;
      if (changingPassword) authPayload.password = newPassword;
      if (newDisplayName) authPayload.data = { display_name: newDisplayName };

      if (Object.keys(authPayload).length > 0) {
        const { data, error: authError } = await supabase.auth.updateUser(authPayload);
        if (authError) throw authError;

        if (data?.user?.email) setEmail(data.user.email);

        const { data: sessionData, error: sessionError } = await supabase.auth.refreshSession();
        if (sessionError) console.warn("Failed to refresh session:", sessionError);
        else setToken(sessionData?.session?.access_token || null);
      }

      const { data: updatedUser, error: refreshError } = await supabase
        .from("users")
        .select("*")
        .eq("id", currentUser.id)
        .single();
      if (refreshError) throw refreshError;

      setCurrentUser(updatedUser);
      setUsername(updatedUser.display_name || updatedUser.email);

      // Clear password values after a successful change. The settings modal stays
      // open and provides its own quiet inline success feedback.
      if (changingPassword) {
        setNewPassword("");
        setConfirmPassword("");
      }

      return updatedUser;
    } catch (err) {
      console.error("Error updating account:", err);
      throw err;
    } finally {
      setUpdatingAccount(false);
    }
  };

  // ------------------------
  // Card Handlers
  // ------------------------
  const handleDeleteItem = async (cardIds) => {
    try {
      const ids = Array.isArray(cardIds) ? cardIds : [cardIds];

      for (const id of ids) {
        await deleteItem(id);
      }

      setCards((prev) =>
        prev.filter(
          (card) => !ids.some((id) => String(id) === String(card.id))
        )
      );

      if (currentUser?.id) {
        fetchCardCounts(currentUser.id);
      }
    } catch (err) {
      console.error("Delete failed:", err);
      throw err;
    }
  };

  const location = useLocation();

  const headerConfig =
    HEADER_CONFIG.find((c) => c.match(location.pathname)) ||
    HEADER_CONFIG[1];

  const { logo, home, menuType } = headerConfig;

  const handleOpenNotifications = () => {
    // Opening the panel should not mark everything read.
    // Individual notifications are marked read when clicked.
    setShowNotifications(true);
  };

 // ------------------------
  // Render
  // ------------------------
  if (!sessionChecked) return <div className="loading">Checking session...</div>;

  return (
    <div className="App">
      <AppHeader
        logo={logo}
        home={home}
        menuType={menuType}
        username={username}
        notificationCount={unreadCount}
        onOpenMenu={() => setShowMenu(true)}
        onOpenNotifications = {handleOpenNotifications}
      />

      {showNotifications && (
          <NotificationPanel
            notifications={notifications}
            onClose={() => setShowNotifications(false)}
            onMarkRead={markAsRead}
            onMarkAllRead={markAllAsRead}
          />
        )}
     
      <AppSidebar
          show={showMenu}
          onClose={() => setShowMenu(false)}
          isAuthenticated={!!currentUser}
          isAdmin={isAdmin}
          canAccessAdminConsole={canAccessAdminConsole}
          sellerAccess={sellerAccess}
          onNavigate={(path) => {
            navigate(path);
            setShowMenu(false);
          }}
          onLogout={handleLogout}
          onOpenSettings={() => {
            setShowSettings(true);
            setShowMenu(false);
          }}
          onOpenAdmin={() => {
            navigate("/admin");
            setShowMenu(false);
          }}
        />


      {/* Modals */}
      {currentUser && (
        <>
          <AccountSettingsModal
              show={showSettings}
              onClose={() => setShowSettings(false)}
              currentUser={currentUser}
              userEmail={email}       // <-- pass the auth email separately
              username={username}
              newDisplayName={newDisplayName}
              setNewDisplayName={setNewDisplayName}
              newEmail={newEmail}
              setNewEmail={setNewEmail}
              newPassword={newPassword}
              setNewPassword={setNewPassword}
              confirmPassword={confirmPassword}
              setConfirmPassword={setConfirmPassword}
              handleUpdateAccount={handleUpdateAccount}
              updatingAccount={updatingAccount}
              isDarkMode={isDarkMode}
              setIsDarkMode={setIsDarkMode}
            />
        </>
      )}

     {/* HobbliVision Modal */}
      {showHobbliVision && (
        <HobbliVisionModal
          open={showHobbliVision}
          user={currentUser}
          onClose={() => setShowHobbliVision(false)}
          onAddItem={async () => {
            await refreshCards();

            if (currentUser?.id) {
              fetchCardCounts(currentUser.id);
            }
          }}
          onItemConfirmed={() => {}}
        />
      )}

      {/* Add Card Modal */}
      {currentUser && showAddCard && (
        <div className="modal-overlay" onClick={() => setShowAddCard(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <CardFormWrapper
              user={currentUser}
              cardData={addCardData}
              onAddItem={async () => {
                await refreshCards();
                if (currentUser?.id) fetchCardCounts(currentUser.id);
                setAddCardData(null);
              }}
              onCancel={() => {
                setShowAddCard(false);
                setAddCardData(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <main>
          <AppRoutes
            token={token}
            userItemCount={userItemCount}
            cards={cards}
            collections={collections}
            handleDeleteItem={handleDeleteItem}
            refreshCards={refreshCards}
            refreshCollections={() => fetchCollections(currentUser?.id)}
            currentUser={currentUser}
            sellerAccess={sellerAccess}
            canAccessAdminConsole={canAccessAdminConsole}
            authMode={authMode}
            setAuthMode={setAuthMode}
            handleLoginSupabase={handleLoginSupabase}
            handleRegisterSupabase={handleRegisterSupabase}
            navigate={navigate}
            sessionChecked={sessionChecked}
            fetchUnreadMessages={fetchUnreadMessages}
            showHobbliVision={showHobbliVision}
            setShowHobbliVision={setShowHobbliVision}
          />
        </main>
    </div>
  );

}

export default App;
